// ExpandedProjectView.jsx

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Card,
  Container,
  Row,
  Col,
  Form,
  Button,
  Modal,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  FaCalendarAlt,
  FaUserAlt,
  FaUsers,
  FaClock,
  FaEdit,
  FaCheckCircle,
  FaHourglassHalf,
  FaTrash,
} from "react-icons/fa";
import "../ExpandedProjectView.css";
import { useUser } from "../contextProviders/userContext";
import { API_BASE_URL } from "../api";
import { deduplicateReflections } from "../utils/reflections";

const ExpandedProjectView = ({ project }) => {
  const [reflection, setReflection] = useState("");
  const [pictures, setPictures] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [participationData, setParticipationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [reflections, setReflections] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingReflectionId, setDeletingReflectionId] = useState(null);
  const [reflectionError, setReflectionError] = useState("");
  const savingRef = useRef(false);
  const submissionIdRef = useRef(null);
  const { user } = useUser();
  const projectReflections = deduplicateReflections(reflections);

  useEffect(() => {
    fetchParticipationData();
    if (project?.reflections) {
      setReflections(project.reflections);
    } else {
      setReflections([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchParticipationData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/participations/${project._id}`,
        { headers: getAuthHeader() }
      );
      setParticipationData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching participation data:", error);
      setError("Failed to fetch participation data. Please try again later.");
      setLoading(false);
    }
  };

  const handleReflectionChange = (e) => {
    setReflection(e.target.value);
  };

  const handlePictureUpload = (e) => {
    const files = Array.from(e.target.files);
    setPictures([...pictures, ...files].slice(0, 3)); // Ensure max 3 pictures
  };

  const handleSaveReflection = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    let saved = false;

    try {
      setSaveError(null);

      // Check if user is authenticated
      const userId = user.id;
      console.log(userId);
      if (!userId) {
        console.log("User not authenticated. Please log in again.");
        setSaveError("User not authenticated. Please log in again.");
        return;
      }

      if (!reflection.trim() && pictures.length === 0) {
        setSaveError("Add a written reflection or at least one evidence image.");
        return;
      }

      // Create FormData and append all necessary fields
      const formData = new FormData();
      formData.append("reflection", reflection);
      formData.append("submissionId", submissionIdRef.current);

      // Append pictures if any
      pictures.forEach((picture, index) => {
        formData.append("pictures", picture, picture.name);
      });

      // Make the API request
      const response = await axios.post(
        `${API_BASE_URL}/projects/${project._id}/reflections`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Check if the response indicates success
      if (response.data.success) {
        saved = true;
        // Show success message
        setSuccessMessage(
          response.data.message || "Reflection added successfully"
        );

        // Add the new reflection directly to the local reflections state to trigger an instant UI rerender!
        const newReflectionObj = response.data.data;
        if (newReflectionObj) {
          setReflections((previous) =>
            previous.some(
              (item) =>
                item._id &&
                newReflectionObj._id &&
                item._id.toString() === newReflectionObj._id.toString()
            )
              ? previous
              : [newReflectionObj, ...previous]
          );
        }

        // Clear form and close modal
        setReflection("");
        setPictures([]);

        // Close modal after a delay
        setTimeout(() => {
          setShowModal(false);
          setSuccessMessage("");
          setIsSaving(false);
          savingRef.current = false;
          submissionIdRef.current = null;
        }, 2000);

        // Refresh project data to display new reflections
        fetchParticipationData();
      } else {
        setSaveError("Failed to save reflection. Please try again.");
      }
    } catch (error) {
      console.error("Error saving reflection:", error);
      setSaveError(
        error.response?.data?.error ||
          "Failed to save reflection. Please try again."
      );
    } finally {
      if (!saved) {
        setIsSaving(false);
        savingRef.current = false;
      }
    }
  };

  const handleEditClick = () => {
    submissionIdRef.current =
      window.crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setSaveError(null);
    setSuccessMessage("");
    setShowModal(true);
  };

  const handleDeleteReflection = async (reflectionId) => {
    if (!reflectionId || deletingReflectionId) return;
    const confirmed = window.confirm(
      "Delete this reflection? This action cannot be undone."
    );
    if (!confirmed) return;

    setDeletingReflectionId(reflectionId);
    setReflectionError("");
    try {
      await axios.delete(
        `${API_BASE_URL}/projects/${project._id}/reflections/${reflectionId}`,
        { headers: getAuthHeader() }
      );
      setReflections((current) =>
        current.filter(
          (item) => item._id?.toString() !== reflectionId.toString()
        )
      );
    } catch (deleteError) {
      setReflectionError(
        deleteError.response?.data?.error ||
          "Failed to delete reflection. Please try again."
      );
    } finally {
      setDeletingReflectionId(null);
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleImageModalClose = () => {
    setSelectedImage(null);
    setShowImageModal(false);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  const approvedHours = participationData?.logs.reduce(
    (sum, log) => sum + (log.status === "approved" ? log.hours : 0),
    0
  );

  const pendingHours = participationData?.logs.reduce(
    (sum, log) => sum + (log.status === "pending" ? log.hours : 0),
    0
  );

  const totalHours = approvedHours + pendingHours;

  return (
    <Container className="mt-4">
      {/* Project Details with Hours */}
      <Card className="modern-card">
        <Card.Body>
          <Card.Title className="text-primary mb-1">{project.name}</Card.Title>
          <p style={{ fontSize: "0.9rem", color: "var(--color-text-secondary)", marginBottom: "1.25rem" }}>
            {project.description}
          </p>
          <Row className="mb-2 g-2">
            <Col xs={12} md={6}>
              <div className="stat-row"><FaCalendarAlt className="stat-icon" style={{ color: "var(--color-primary)" }} />
                <span><strong>Start Date:</strong> {new Date(project.startDate).toLocaleDateString()}</span>
              </div>
              {project.endDate && (
                <div className="stat-row"><FaCalendarAlt className="stat-icon" style={{ color: "var(--color-primary)" }} />
                  <span><strong>End Date:</strong> {new Date(project.endDate).toLocaleDateString()}</span>
                </div>
              )}
              <div className="stat-row"><FaHourglassHalf className="stat-icon" style={{ color: "var(--color-gold)" }} />
                <span className="status-pending">Pending Hours: {pendingHours.toFixed(2)} hrs</span>
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="stat-row"><FaUserAlt className="stat-icon" style={{ color: "var(--color-primary)" }} />
                <span><strong>Organiser:</strong> {project.organiserName} ({project.organiserType})</span>
              </div>
              <div className="stat-row"><FaClock className="stat-icon" style={{ color: "var(--color-text-muted)" }} />
                <span><strong>Total Hours Logged:</strong> {totalHours.toFixed(2)} hrs</span>
              </div>
              <div className="stat-row"><FaCheckCircle className="stat-icon" style={{ color: "var(--color-teal)" }} />
                <span className="status-approved">Approved Hours: {approvedHours.toFixed(2)} hrs</span>
              </div>
            </Col>
          </Row>
          <div className="stat-row mt-1"><FaUsers className="stat-icon" style={{ color: "var(--color-primary)" }} />
            <span><strong>Participants:</strong> {project.participants.length}</span>
          </div>
        </Card.Body>
      </Card>

      {/* Logs Table */}
      <Card className="mt-3 modern-card">
        <Card.Body>
          <Card.Title className="text-primary mb-3">Logged Hours</Card.Title>
          <div className="table-responsive">
            <table className="wcco-table">
              <thead>
                <tr>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Hours</th>
                  <th>Reflection & Evidence</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {participationData?.logs.map((log, index) => (
                  <tr key={index}>
                    <td>{new Date(log.startTime).toLocaleString()}</td>
                    <td>{new Date(log.endTime).toLocaleString()}</td>
                    <td><strong>{log.hours.toFixed(2)}</strong></td>
                    <td>
                      {log.reflection && <div className="small">{log.reflection}</div>}
                      {log.evidence?.map((url, evidenceIndex) => (
                        <a key={url} href={url} target="_blank" rel="noreferrer" className="d-inline-block me-2 small">
                          Evidence {evidenceIndex + 1}
                        </a>
                      ))}
                      {!log.reflection && !log.evidence?.length && <span className="text-muted small">None</span>}
                    </td>
                    <td>
                      <span className={`status-cell-${log.status}`}>
                        {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      {/* Reflections Display */}
      <Card className="mt-3 modern-card">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Card.Title className="text-primary mb-0">Project Reflections</Card.Title>
            <Button
              onClick={handleEditClick}
              size="sm"
              style={{
                backgroundColor: "var(--color-primary)",
                border: "none",
                borderRadius: "var(--radius-md)",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "0.4rem"
              }}>
              <FaEdit size={13} /> Add Reflection
            </Button>
          </div>
          {reflectionError && <Alert variant="danger">{reflectionError}</Alert>}
          {projectReflections.length > 0 ? (
            projectReflections.map((reflection, index) => {
              const creatorId = reflection.createdBy?._id || reflection.createdBy;
              const currentUserId = user?.id || user?._id;
              const canDelete =
                user?.role === "admin" ||
                (creatorId && currentUserId && creatorId.toString() === currentUserId.toString());
              const authorName = reflection.createdBy?.name
                ? `${reflection.createdBy.name} ${reflection.createdBy.surname || ""}`.trim()
                : "Project member";
              return (
              <div key={reflection._id || `${reflection.createdAt}-${index}`} className="reflection-card">
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <p style={{ fontSize: "0.92rem", color: "var(--color-text)", marginBottom: "0.5rem" }}>{reflection.text}</p>
                  {canDelete && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      aria-label="Delete reflection"
                      title="Delete reflection"
                      disabled={deletingReflectionId === reflection._id}
                      onClick={() => handleDeleteReflection(reflection._id)}
                    >
                      {deletingReflectionId === reflection._id ? <Spinner animation="border" size="sm" /> : <FaTrash size={12} />}
                    </Button>
                  )}
                </div>
                <small style={{ color: "var(--color-text-muted)" }}>
                  {authorName} · {new Date(reflection.createdAt).toLocaleString()}
                </small>
                {reflection.pictures && reflection.pictures.length > 0 && (
                  <Row className="mt-2">
                    {reflection.pictures.map((picture, picIndex) => (
                      <Col key={picIndex} xs={6} md={4} className="mb-2">
                        <img
                          src={picture}
                          alt={`Reflection ${index + 1} - Picture ${picIndex + 1}`}
                          className="clickable-image"
                          onClick={() => handleImageClick(picture)}
                        />
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
              );
            })
          ) : (
            <p style={{ color: "var(--color-text-muted)", fontSize: "0.9rem" }}>No project reflections have been shared yet.</p>
          )}
        </Card.Body>
      </Card>

      {/* Image Modal */}
      <Modal show={showImageModal} onHide={handleImageModalClose} centered>
        <Modal.Body>
          {selectedImage && (
            <img src={selectedImage} alt="Selected" className="img-fluid" />
          )}
        </Modal.Body>
      </Modal>

      {/* Modal for Adding/Editing Reflection */}
      <Modal show={showModal} onHide={() => !isSaving && setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add/Edit Reflection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saveError && <Alert variant="danger">{saveError}</Alert>}

          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          <Form>
            <Form.Group>
              <Form.Label>Your Reflection (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={reflection}
                onChange={handleReflectionChange}
                placeholder="Write your reflection here..."
                disabled={isSaving}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Evidence Pictures (Optional, Max 3)</Form.Label>
              <Form.Control
                type="file"
                multiple
                accept="image/*"
                onChange={handlePictureUpload}
                disabled={pictures.length >= 3}
              />
              <div className="d-flex mt-2 flex-wrap">
                {pictures.map((picture, index) => (
                  <img
                    key={index}
                    src={URL.createObjectURL(picture)}
                    alt={`Preview ${index + 1}`}
                    className="img-thumbnail-preview"
                  />
                ))}
              </div>
            </Form.Group>
            <Button
              variant="primary"
              onClick={handleSaveReflection}
              disabled={isSaving}
              className="w-100">
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving…
                </>
              ) : "Save Reflection"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default ExpandedProjectView;
